import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import AsideHeader from '../AsideHeader';
import { animated, useSpring } from 'react-spring';
import useMeasure from '~/hooks/useMeasure';
import useIsBasicForm from '../../../../hooks/useIsBasicForm';
import TEST_ID from './index.testid';
import { eventsState } from '../../../../state/nodesAndEvents';
import { useRecoilState } from 'recoil';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body } from '~/components/atom/Typography';
import type { FormBuilder_Event } from '~/graphql/types';
import hasValue from '~/util/hasValue';
import LeadscoreInput from '~/components/organism/LeadscoreInput';

export type Props = {
  dataTestId?: string;
  expanded?: boolean;
  onToggleExpand: () => void;
};

type onChangeType = {
  field: 'aiOptimization' | 'score';
  value: boolean | number | null;
};

const text = {
  estimatedLeadscoreLabel: 'Leadscore',
  aiLeadscoreLabel: 'AI leadscore',
  description:
    'Na het indienen van dit formulier wordt de leadscore verhoogd met de opgegeven waarde. Hoe hoger de leadscore, hoe warmer de lead.',
};

const Leadscore: React.FCC<Props> = ({
  dataTestId,
  expanded = false,
  onToggleExpand,
}) => {
  const [state, setState] = useRecoilState(eventsState);
  const isBasicForm = useIsBasicForm();

  if (!isBasicForm || !hasValue(state)) return null;

  /* We only have one event per form */
  const event = state[0];

  const onChange = ({ field, value }: onChangeType) => {
    setState(prev => {
      if (!prev) return prev;
      return [{ ...prev[0], [field]: value }];
    });
  };

  return (
    <Content
      dataTestId={dataTestId}
      onToggleExpand={onToggleExpand}
      expanded={expanded}
      event={event}
      onChange={onChange}
    />
  );
};

type ContentProps = {
  dataTestId?: string;
  onToggleExpand: Props['onToggleExpand'];
  expanded: boolean;
  event: FormBuilder_Event;
  onChange: (value: onChangeType) => void;
};

const Content: React.FC<ContentProps> = ({
  dataTestId,
  onToggleExpand,
  expanded,
  event,
  onChange,
}) => {
  const { ref, bounds } = useMeasure();
  const theme = useTheme();
  const spring = useSpring({
    height: expanded ? bounds.height + theme.remToPxRaw(theme.space('m')) : 0,
  });

  return (
    <Container data-testid={dataTestId}>
      <AsideHeader
        dataTestId={TEST_ID.ASIDE_HEADER}
        heading="Leadscore"
        icon="star"
        onClick={onToggleExpand}
        expanded={expanded}
      />
      <OverflowContainer style={spring}>
        <JustificationContainer
          direction="column"
          padding={[null, 'm']}
          width="100%"
          gap="xs"
          ref={ref}
        >
          <Body size="base" margin={[null]}>
            {text.description}
          </Body>
          <LeadscoreInput
            score={{
              value: event.score,
              onChange: (value: number | null) =>
                onChange({
                  field: 'score',
                  value,
                }),
            }}
          />
        </JustificationContainer>
      </OverflowContainer>
    </Container>
  );
};

const Container = styled.aside(
  ({ theme }) => css`
    z-index: ${theme.z('top')};
    user-select: none;
    width: 100%;
  `,
);

const OverflowContainer = styled(animated.div)`
  overflow-x: hidden;
`;

export default Leadscore;
